/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react';

import {
  Avatar as MUIAvatar,
  AvatarProps as MUIAvatarProps,
  Badge as MUIBadge,
} from '@mui/material';
import classNames from 'classnames';

import { useStyles } from './Avatar.styles';

/**
 * Возможные размеры аватара.
 *
 * @typedef {('extraLarge' | 'large' | 'medium' | 'small' | 'extraSmall')} AvatarSize
 */
export type AvatarSize =
  | 'extraLarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'extraSmall';

/**
 * Интерфейс свойств для компонента Avatar.
 *
 * @interface AvatarProps
 * @extends {MUIAvatarProps}
 * @property {boolean} [withBadge=false] - Определяет, будет ли отображаться значок на аватаре.
 * @property {AvatarSize} [size='medium'] - Размер аватара.
 * @property {() => Promise<Blob>} [loadPhoto] - Функция, возвращающая промис, который разрешается в Blob объект, содержащий изображение аватара.
 */
type AvatarProps = MUIAvatarProps & {
  variant?: 'circle' | 'square';
  withBadge?: boolean;
  size?: AvatarSize;
  loadPhoto?: () => Promise<Blob>;
};

/**
 * Настраиваемый компонент аватара, построенный с использованием Material-UI.
 *
 * @component
 * @param {AvatarProps} props - Свойства для компонента Avatar.
 * @param {boolean} [props.withBadge=false] - Определяет, будет ли отображаться значок на аватаре.
 * @param {AvatarSize} [props.size='medium'] - Размер аватара.
 * @param {React.ReactNode} [props.children] - Содержимое, отображаемое внутри аватара.
 * @param {() => Promise<Blob>} [props.loadPhoto] - Функция, возвращающая промис, который разрешается в Blob объект, содержащий изображение аватара.
 * @param {string} [props.alt] - Альтернативный текст для изображения аватара.
 * @returns {JSX.Element} Компонент Avatar.
 */

export const Avatar = (props: AvatarProps) => {
  const {
    variant = 'circle',
    withBadge,
    size = 'medium',
    children,
    loadPhoto,
    alt,
    src: url,
  } = props;

  const { classes } = useStyles();

  const [src, setSrc] = useState(url);

  useEffect(() => {
    if (!src) {
      loadPhoto?.()
        .then((blob) => {
          setSrc(URL.createObjectURL(blob));
        })
        .catch(() => {
          // do nothing
        });
    }
  }, [loadPhoto, src]);

  return withBadge ? (
    <MUIBadge
      className={`aqa_avatar_badge ${classes.badge}`}
      overlap="circular"
      variant="dot"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Avatar
        className={classNames(
          'aqa_avatar',
          variant === 'square' && classes.avatarSquare,
        )}
        alt={alt}
        children={children}
        src={src}
        size={size}
      />
    </MUIBadge>
  ) : (
    <MUIAvatar
      src={src}
      alt={alt}
      children={src ? <></> : children}
      data-testid="avatar"
      className={classNames(
        'aqa_avatar',
        classes.avatar,
        classes[size],
        variant === 'square' && classes.avatarSquare,
      )}
    />
  );
};
